// Port of Flutter's mainScreen.dart — the "Your Locations" tab.
//
// A grid of location cards (maxCrossAxisExtent 230): ButtonColor card, radius
// 20, elevation 6, a 50px location_city icon over the location name. Tap opens
// the location's rooms; LONG-PRESS opens its address sheet. The assistance
// contact line is pinned to the bottom, and the FAB carries "Add Location".

import React from 'react';
import {
  Box, Card, CardActionArea, Typography, CircularProgress, useTheme,
} from '@mui/material';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../../components/MobileAppBar';
import CircleMenu from '../../components/CircleMenu';
import ExtentGrid from '../../components/ExtentGrid';
import useLongPress from '../../components/useLongPress';
import { useApp } from '../../../../core/state/AppContext';

function LocationCard({ location, onOpen, onEdit }) {
  const theme = useTheme();
  const c = theme.sli;
  const press = useLongPress(onEdit, onOpen);

  return (
    <Card elevation={6} sx={{ bgcolor: c.button, borderRadius: '20px', height: '100%', m: '5px' }}>
      <CardActionArea
        {...press}
        sx={{
          height: '100%', borderRadius: '20px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', p: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <LocationCityIcon sx={{ color: c.text, fontSize: 50 }} />
          <Box sx={{ height: 16 }} />
          <Typography
            sx={{
              color: c.text,
              fontSize: 15,
              fontWeight: 700,
              textAlign: 'center',
              textShadow: '1px 1px 4px rgba(0,0,0,0.54)',
            }}
          >
            {location.LocationName}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}

export default function LocationsTab() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { user, locations, locationsLoading } = useApp();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back }}>
      <CenterTitleBar title="Your Locations" />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative' }}>
        <Box sx={{ m: 2 }}>
          {locationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: c.text }} />
            </Box>
          ) : locations.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography sx={{ color: c.text, fontSize: 20 }}>No Locations Found</Typography>
            </Box>
          ) : (
            <ExtentGrid maxExtent={230} childAspectRatio={1} gap={0}>
              {locations.map((l) => (
                <LocationCard
                  key={l.LocationID}
                  location={l}
                  onOpen={() => navigate(`/rooms/${l.LocationID}`, {
                    state: { locationName: l.LocationName },
                  })}
                  onEdit={() => navigate(`/location/${l.LocationID}`)}
                />
              ))}
            </ExtentGrid>
          )}
        </Box>

        {/* Pinned assistance line — Flutter aligns this to the bottom of the body. */}
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography sx={{ color: c.text }}>
            {`For Assistance Contact: ${user?.AssistanceContact ?? ''}`}
          </Typography>
        </Box>
      </Box>

      <CircleMenu
        items={[{
          icon: <AddLocationAltOutlinedIcon />,
          title: 'Add Location',
          onClick: () => navigate('/location/new'),
        }]}
      />
    </Box>
  );
}
